/**
 * KAN-988: обёртка маршрута по функции тарифа. Функция есть - страница; нет -
 * экран "Функция <подпись> доступна в тарифах Продажи и Про" и кнопка смены
 * тарифа (менеджеру вместо кнопки: обратиться к владельцу кабинета и кнопка
 * "Выйти": на /m у него другого выхода нет, раскладка не рисуется). Пока
 * /auth/me грузится, ничего не рисуется: страница не шлёт запросов, на которые
 * backend ответит 403.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAccounts } from '@/hooks/useAccounts';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useFeature } from '@/hooks/useFeature';
import { featureLabel } from '@/lib/features';
import { isManagerUser } from '@/lib/managerAccess';

interface FeatureGuardProps {
  feature: string;
  children: ReactNode;
}

export function FeatureGuard({ feature, children }: FeatureGuardProps) {
  // Без деструктуризации: тесты маршрутов App подменяют useQuery пустым моком.
  const current = useCurrentUser();
  const user = current?.data;
  const enabled = useFeature(feature);

  if (current?.isLoading) {
    return null;
  }

  if (enabled) {
    return <>{children}</>;
  }

  return <FeatureUnavailable feature={feature} isManager={isManagerUser(user)} />;
}

interface FeatureUnavailableProps {
  feature: string;
  isManager: boolean;
}

function FeatureUnavailable({ feature, isManager }: FeatureUnavailableProps) {
  return (
    <div
      className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 py-16 text-center"
      data-testid="feature-unavailable">
      <Lock className="h-10 w-10 text-gray-400" aria-hidden="true" />
      <p className="text-base font-medium text-gray-800">
        Функция {featureLabel(feature)} доступна в тарифах Продажи и Про
      </p>
      {isManager ? (
        <ManagerLockedActions />
      ) : (
        <Link
          to="/billing"
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
          Сменить тариф
        </Link>
      )}
    </div>
  );
}

/** Менеджеру: к кому обратиться и выход тем же logout, что в переключателе аккаунтов. */
function ManagerLockedActions() {
  const { logout } = useAccounts();
  return (
    <>
      <p className="text-sm text-gray-600">Обратитесь к владельцу кабинета.</p>
      <Button variant="outline" size="sm" onClick={logout}>
        Выйти
      </Button>
    </>
  );
}
